import { assign, noop } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import ModalRenderer, {
  IModal,
  IModalImagePreview,
  TField,
} from './modal-renderer';
import {
  OVERLAY_CHROME_LEAD_MS,
  setOverlayChromePrimed,
} from '../../hooks/use-theme-color/use-theme-color';
import MultiStepModalRenderer, {
  IMultiStepModal,
} from './multi-step-modal-renderer';

interface IModalProviderProps {
  children: ReactNode;
}

interface IModalContext {
  save: <
    T extends Partial<
      Record<
        keyof T,
        TField | TField[] | Record<string, TField> | Record<string, TField>[]
      >
    >,
  >(
    t: IModal<T>,
  ) => void;
  ask: (t: IModal) => void;
  info: (t: IModal) => void;
  destroy: () => void;
  payment: (t: IModal) => void;
  readOnly: <
    T extends Partial<
      Record<
        keyof T,
        TField | TField[] | Record<string, TField> | Record<string, TField>[]
      >
    >,
  >(
    t: IModal<T>,
  ) => void;
  multiStep: <TData = Record<string, unknown>>(
    t:
      | IMultiStepModal<TData>
      | ((onClose: () => void) => IMultiStepModal<TData>),
  ) => void;
  /** Open a full-size image preview dialog. */
  preview: (t: IModalImagePreview) => void;
  isOpen?: boolean;
}

const ModalContext = createContext<IModalContext>({
  save: noop,
  ask: noop,
  info: noop,
  destroy: noop,
  payment: noop,
  readOnly: noop,
  multiStep: noop,
  preview: noop,
  isOpen: false,
});

const useModal = () => useContext(ModalContext);

function ModalProvider({ children }: IModalProviderProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [modalState, setModalState] = useState<IModal<any>>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [multiStepModalState, setMultiStepModalState] =
    useState<IMultiStepModal<any>>();

  /**
   * Recolour the browser chrome, hold a beat, then mount.
   *
   * Once a modal is up the page can't scroll, and iOS Safari stops re-reading
   * its chrome colours — recolour at that point and nothing happens until the
   * modal closes, which reads as the header changing at exactly the wrong
   * moments. Recolouring first, while the page is still live, gives Safari a
   * window to pick it up before everything freezes.
   */
  const setModal = useCallback((m?: IModal<any>) => {
    if (!m) {
      setModalState(undefined);
      setOverlayChromePrimed(false);
      return;
    }
    setOverlayChromePrimed(true);
    window.setTimeout(() => setModalState(m), OVERLAY_CHROME_LEAD_MS);
  }, []);

  const save: <
    T extends Partial<
      Record<
        keyof T,
        TField | TField[] | Record<string, TField> | Record<string, TField>[]
      >
    >,
  >(
    m: IModal<T>,
  ) => void = useCallback((m) => {
    setModal(assign({ type: 'save' }, m));
  }, []);

  const ask: (m: IModal) => void = useCallback((m) => {
    setModal(m);
  }, []);

  const info: (m: IModal) => void = useCallback((m) => {
    setModal(m);
  }, []);

  const destroy = useCallback(() => setModal(undefined), []);

  const payment: (m: IModal) => void = useCallback((m) => {
    setModal(m);
  }, []);

  const readOnly: <
    T extends Partial<
      Record<
        keyof T,
        TField | TField[] | Record<string, TField> | Record<string, TField>[]
      >
    >,
  >(
    m: IModal<T>,
  ) => void = useCallback((m) => {
    setModal(assign({ type: 'readOnly' }, m));
  }, []);

  const multiStepModal = useCallback(
    <TData = Record<string, unknown>,>(
      m:
        | IMultiStepModal<TData>
        | ((onClose: () => void) => IMultiStepModal<TData>),
    ) => {
      const config =
        typeof m === 'function'
          ? m(() => setMultiStepModalState(undefined))
          : m;
      setMultiStepModalState(config);
      setModal(undefined); // Close regular modal if open
    },
    [],
  );

  const isOpen = useMemo(
    () => !!modalState || !!multiStepModalState,
    [modalState, multiStepModalState],
  );

  const preview: (m: IModalImagePreview) => void = useCallback((m) => {
    setModal(m as IModal<any>);
  }, []);

  const contextValue = useMemo(
    () => ({
      save,
      ask,
      info,
      destroy,
      payment,
      readOnly,
      multiStep: multiStepModal,
      preview,
      isOpen,
    }),
    [
      save,
      ask,
      info,
      destroy,
      payment,
      readOnly,
      multiStepModal,
      preview,
      isOpen,
    ],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modalState && (
        // Key by modal type so switching between an `imagePreview` modal (which
        // bypasses the standard chrome and its hooks via an early return) and a
        // standard modal remounts the renderer instead of changing the number
        // of hooks rendered by a single instance ("rendered fewer hooks" crash).
        <ModalRenderer
          key={modalState.type}
          modal={modalState}
          onClose={() => setModal(undefined)}
        />
      )}
      {multiStepModalState && (
        <MultiStepModalRenderer
          modal={multiStepModalState}
          onClose={() => setMultiStepModalState(undefined)}
        />
      )}
    </ModalContext.Provider>
  );
}

export { ModalContext, ModalProvider, useModal };
export type { IModalContext, IModalProviderProps };
export { useMultiStepModal } from './multi-step-modal-renderer';
export type {
  IMultiStepModal,
  IMultiStepModalStep,
  IMultiStepActions,
} from './multi-step-modal-renderer';
